import type {
  ColorByCategory,
  ColorByRule,
} from 'components/color-by-attribute-modal';
import {
  deriveValues,
  refreshRule,
  resolveColors,
  type ColorableItem,
  type SectionColorRule,
} from './attribute-colors';
import type { GroupedWell } from './utils';
import type { CrossSectionSubject } from './types';
import { WELL_STATUS_COLOR_MAP } from 'utils/well-status';

/**
 * Colours for the wells the section crosses. The section does not invent what a well
 * colour means — the map's colour-by rule does, and the two must never disagree. This is
 * that rule, kept in the rail beside the grids' one.
 */

/** Navy, which is what the map draws a well trace in. */
export const DEFAULT_WELL_COLOR = '#003a6d';

/**
 * The map's own status colours (wells-v2-layer), so a Producing well is the same
 * blue on the section as on the map beside it. These are the defaults the seeded
 * Status rule opens with; every swatch stays editable.
 */
export const WELL_STATUS_COLORS: Record<string, string> = {
  producing: '#4292c6',
  duc: '#fe9929',
  permit: '#ff69b4',
  shut_in: '#555555',
  expired_permit: '#969696',
  inactive: '#cccccc',
  // The Create New Well statuses, from the shared colour file (INS-13300).
  proposal: WELL_STATUS_COLOR_MAP.proposal,
  planned: WELL_STATUS_COLOR_MAP.planned,
  pre_permit: WELL_STATUS_COLOR_MAP.pre_permit,
  apd: WELL_STATUS_COLOR_MAP.apd,
};

/**
 * Handed out to values the rule has not seen before. Distinct hues rather than a ramp:
 * these are categories, not a scale.
 */
const WELL_COLORS = [
  '#4292c6',
  '#fe9929',
  '#ff69b4',
  '#555555',
  '#8a3ffc',
  '#24a148',
  '#d12771',
  '#08bdba',
];

/**
 * The attributes a well colour rule can be written over — the three the response
 * carries per crossing. Hole direction is deliberately absent: the cut does not
 * report it, and a category with nothing behind it is worse than no category.
 */
export const WELL_COLOR_CATEGORIES: ColorByCategory[] = [
  { id: 'status', label: 'Status' },
  { id: 'operator', label: 'Current Operator' },
  { id: 'landingZone', label: 'SIM Landing Zone' },
];

/**
 * Where a well colour rule paints. The section's wells are drawn twice — in the plot and
 * on the map — so one rule covers both. Wells the section does not touch keep the map's
 * own styling, the rule's values being derived from the section alone.
 */
export const WELL_COLOR_TARGETS = [
  { id: 'crossSection', label: 'Cross Section' },
  { id: 'map', label: 'Map' },
];

/**
 * The default colour for the nth value of a well rule.
 *
 * @param {number} index - The value's position in the rule's list.
 * @return {string} A hex colour.
 */
export const defaultWellColor = (index: number): string => {
  if (index === 0) {
    return DEFAULT_WELL_COLOR;
  }
  return WELL_COLORS[(index - 1) % WELL_COLORS.length];
};

/**
 * A crossing as something a rule can colour. Keyed by uwi rather than by crossing, so
 * every crossing of one well takes the same colour — they are one well.
 *
 * @param {GroupedWell} well - The crossing.
 * @return {ColorableItem} The well, in the shared shape.
 */
const asColorable = (well: GroupedWell): ColorableItem => ({
  id: well.uwi,
  attributes: wellAttributes(well),
});

/**
 * What a crossing is worth per attribute. Exported because the rail's filters ask
 * the same question of the same crossing, and the two must not drift — a well the
 * filter reads as Producing has to be the one the colour rule paints as Producing.
 *
 * @param {GroupedWell} well - The crossing.
 * @return {Record<string, string | null>} The attribute values, by attribute id.
 */
export const wellAttributes = (
  well: GroupedWell
): Record<string, string | null> => ({
  status: well.status,
  operator: well.header.operator,
  landingZone: well.header.landingZone,
});

/**
 * The values a well attribute takes across the crossings on the section.
 *
 * @param {Array} wells - The crossings.
 * @param {string} attribute - The attribute id.
 * @return {Array} The distinct values, in crossing order.
 */
export const deriveWellColorValues = (
  wells: GroupedWell[],
  attribute: string
): string[] => deriveValues(wells.map(asColorable), attribute);

/**
 * Brings a well rule up to date with the crossings on the section.
 *
 * @param {SectionColorRule} rule - The rule as it stands.
 * @param {Array} wells - The crossings.
 * @return {SectionColorRule} The rule, with its values and colours filled in.
 */
export const refreshWellColorRule = (
  rule: SectionColorRule,
  wells: GroupedWell[]
): SectionColorRule =>
  refreshRule(rule, wells.map(asColorable), (index, value) => {
    if (rule.attribute === 'status') {
      return WELL_STATUS_COLORS[value] ?? defaultWellColor(index);
    }
    return defaultWellColor(index);
  });

/**
 * The colour each crossing well draws in, on the surface named.
 *
 * @param {Array} wells - The crossings.
 * @param {Array} rules - The rail's well colour rules.
 * @param {string} target - The surface being painted; the section by default.
 * @return {Map} Well uwi to colour.
 */
export const resolveWellColors = (
  wells: GroupedWell[],
  rules: ColorByRule[],
  target = 'crossSection'
): Map<string, string> =>
  resolveColors(
    wells.map(asColorable),
    rules,
    target,
    // Without a rule every well is the same navy: a per-well colour with no rule
    // behind it would be a meaning the section invented.
    () => DEFAULT_WELL_COLOR
  );

/**
 * The colour each defining well draws in, by uwi.
 *
 * The plane's own wells are not crossings, so they are absent from the rail's list —
 * but a Producing well should read the same wherever it appears, so the rail's rule
 * is applied to them too, over the one attribute a vertex label carries. A status the
 * rule has no colour for falls back to the same navy the map draws a well in.
 *
 * @param {Array} vertexLabels - The subject's vertex labels.
 * @param {Array} rules - The rail's Wells rules.
 * @return {Map} Uwi to colour.
 */
export const resolveDefiningWellColors = (
  vertexLabels: CrossSectionSubject['vertexLabels'],
  rules: ColorByRule[]
): Map<string, string> =>
  resolveColors(
    vertexLabels.flatMap((vertex) => {
      if (vertex.uwi === null) {
        return [];
      }
      return [{ id: vertex.uwi, attributes: { status: vertex.status } }];
    }),
    rules,
    'crossSection',
    () => DEFAULT_WELL_COLOR
  );
